/**
 * PathMovementComponent - animates a component along a predefined curve.
 * Moves the owning SceneComponent along a THREE.Curve at a configurable speed.
 * Supports looping or ping-pong behavior. Updates both world position and kinematic
 * physics velocity so physics-aware actors remain in sync.
 */
import * as THREE from 'three';

import { PhysicsVectorParam } from '../../physics/PhysicsEngine.js';
import { PhysicsMotionType } from '../../physics/PhysicsTypes.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';
import { PrimitiveComponent } from '../PrimitiveComponent.js';
import { SceneComponent } from '../SceneComponent.js';

import type { SceneComponentOptions } from '../SceneComponent.js';


export interface PathMovementComponentOptions extends SceneComponentOptions {
  path: THREE.Curve<THREE.Vector3>;
  speed: number;
  loop: 'NoLoop' | 'Loop' | 'PingPong';
  autoStart: boolean;
}

@EngineClass('PathMovementComponent')
export class PathMovementComponent extends SceneComponent {


  @property()
  public path!: THREE.Curve<THREE.Vector3>;

  @property()
  public speed: number = 5;

  @property()
  public loop: 'NoLoop' | 'Loop' | 'PingPong' = 'NoLoop';

  @property()
  public autoStart: boolean = false;

  private currentProgress: number = 0;
  private rootComponent: SceneComponent | null = null;
  private moving: boolean = false;
  private direction: number = 1; // 1 = forward, -1 = backward (for PingPong)

  public override initialize(options?: PathMovementComponentOptions): void {
    super.initialize(options);
    this.path = options?.path ?? this.path;
    this.speed = options?.speed ?? this.speed;
    this.loop = options?.loop ?? this.loop;
    this.autoStart = options?.autoStart ?? this.autoStart;
  }

  public override beginPlay(): void {
    super.beginPlay();
    this.rootComponent = this.getActor()?.rootComponent ?? null;
    if (this.autoStart) {
      this.startMovement();
    }
  }

  public override endPlay(): void {
    super.endPlay();
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);

    if (!this.moving) return;

    const pathLength = this.path.getLength();
    if (pathLength === 0) return;

    this.updateProgress(deltaTime, pathLength);
    this.updatePosition(deltaTime);
  }

  private updateProgress(deltaTime: number, pathLength: number): void {
    const deltaProgress = (this.speed * deltaTime * this.direction) / pathLength;
    this.currentProgress += deltaProgress;

    switch (this.loop) {
      case 'NoLoop':
        this.handleNoLoop();
        break;
      case 'Loop':
        this.handleLoop();
        break;
      case 'PingPong':
        this.handlePingPong();
        break;
    }
  }

  private handleNoLoop(): void {
    if (this.currentProgress >= 1) {
      this.currentProgress = 1;
      this.stopMovement();
    } else if (this.currentProgress <= 0) {
      this.currentProgress = 0;
      this.stopMovement();
    }
  }

  private handleLoop(): void {
    if (this.currentProgress > 1) {
      this.currentProgress -= 1;
    } else if (this.currentProgress < 0) {
      this.currentProgress += 1;
    }
  }

  private handlePingPong(): void {
    if (this.currentProgress > 1) {
      this.currentProgress = 1;
      this.direction = -1;
    } else if (this.currentProgress < 0) {
      this.currentProgress = 0;
      this.direction = 1;
    }
  }

  private updatePosition(deltaTime: number): void {
    const position = this.path.getPoint(THREE.MathUtils.clamp(this.currentProgress, 0, 1));
    if (this.rootComponent) {
      const prev = new THREE.Vector3();
      this.rootComponent.getWorldPosition(prev);
      const delta = position.clone().sub(prev);
      this.rootComponent.addWorldPosition(delta);

      this.updatePhysicsVelocity(delta, deltaTime);
    }
  }

  private updatePhysicsVelocity(delta: THREE.Vector3, deltaTime: number): void {
    if (!this.rootComponent || !(this.rootComponent instanceof PrimitiveComponent)) return;

    const physicsOptions = this.rootComponent.getPhysicsOptions();
    if (physicsOptions?.enabled &&
      (physicsOptions.motionType === PhysicsMotionType.KinematicPositionBased ||
        physicsOptions.motionType === PhysicsMotionType.KinematicVelocityBased)) {
      let velocity: [number, number, number] = [0, 0, 0];
      if (delta.lengthSq() >= 1e-8) {
        velocity = [delta.x / deltaTime, delta.y / deltaTime, delta.z / deltaTime];
        velocity = velocity.map(v => Math.abs(v) < 1e-5 ? 0 : v) as [number, number, number];
      }
      this.rootComponent.setPhysicsVectorParam(PhysicsVectorParam.LinearVelocity, velocity);
    }
  }

  public override tickPostPhysics(deltaTime: number): void {
    super.tickPostPhysics(deltaTime);
  }

  public startMovement(): void {
    this.moving = true;
    this.direction = 1;
  }

  public stopMovement(): void {
    this.moving = false;
  }

  public isMoving(): boolean {
    return this.moving;
  }

  public setProgress(progress: number): void {
    this.currentProgress = THREE.MathUtils.clamp(progress, 0, 1);
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Path';
  }
}